import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import { ISSUES_MALWARE, US_BANK_FIX_PLAN_HEADERS } from '../../../../shared/featureFlags/flagNames';
import { allFixPlanOptionKeys, type FixPlanOptionKey } from '../../../../shared/reports';
import { useQuery } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { selectOptionFactory } from '../../../utils';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import FilterPanel from '../../generic/Filter/FilterPanel';
import { FixPlanReportOptionDetails } from '../../projects/generic/ReportGenerationV2/utils';
import type { SelectOption, SelectOptionWithAttributes } from '../../../types';
import type { ReportsV3QueryParams } from '../types';

/**
 * Keys that are ON by default when the remediation guidance page first loads.
 */
const DEFAULT_ON_KEYS: readonly FixPlanOptionKey[] = [
  'includeQuickWins',
  'includeHighPriority',
  'includeLowPriority',
  'includeOutdatedDependencies',
];

/**
 * Renders fix plan checkboxes for the remediation guidance report.
 * All options are flat (no nesting). State is managed via URL query params.
 */
export const RemediationGuidanceOptions: VoidFunctionComponent = () => {
  const isMalwareEnabled = useIsFeatureEnabled(ISSUES_MALWARE);
  const useAlternativeTitles = useIsFeatureEnabled(US_BANK_FIX_PLAN_HEADERS);
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  // Seed defaults once: main sections ON, others OFF.
  // Uses a ref guard so we only seed once, but keeps [query, setQuery] in deps
  // to avoid a race condition with useReportFormatSelector's effect on mount.
  const hasSeeded = useRef(false);

  useEffect(() => {
    if (hasSeeded.current) {
      return;
    }

    const queryRecord = query as Record<string, string | undefined>;
    const hasAnyFixPlanParam = allFixPlanOptionKeys.some((key) => queryRecord[key] === 'true');

    if (hasAnyFixPlanParam) {
      hasSeeded.current = true;
      return;
    }

    const defaults = Object.fromEntries(
      DEFAULT_ON_KEYS.map((key) => [key, 'true']),
    );
    setQuery({ ...query, ...defaults });
  }, [query, setQuery]);

  const options = useMemo<SelectOptionWithAttributes[]>(() =>
    allFixPlanOptionKeys
      .filter((key) => key !== 'includeMalware' || isMalwareEnabled)
      .map((key) => {
        const detail = FixPlanReportOptionDetails[key];
        return {
          label: useAlternativeTitles ? detail.alternativeTitle : detail.title,
          value: key,
          tooltip: detail.description
            ? <span className="text-align-left">{detail.description}</span>
            : undefined,
        };
      }),
  [isMalwareEnabled, useAlternativeTitles]);

  const values = useMemo<SelectOption[]>(() => {
    const queryRecord = query as Record<string, string | undefined>;
    return allFixPlanOptionKeys
      .filter((key) => queryRecord[key] === 'true')
      .map((key) => selectOptionFactory(key));
  }, [query]);

  const onChange = useCallback((selected: SelectOption[]) => {
    const selectedSet = new Set(selected.map((v) => v.value));
    const update = allFixPlanOptionKeys.reduce<Record<string, string | undefined>>((acc, key) => {
      acc[key] = selectedSet.has(key) ? 'true' : undefined;
      return acc;
    }, {});
    setQuery({ ...query, ...update });
  }, [query, setQuery]);

  return (
    <FilterPanel title="Options">
      <div className="width-full">
        <FilterCardCheckboxes
          label="Fix Plan Sections"
          options={options}
          values={values}
          onChange={onChange}
          tooltipPlacement="left"
        />
      </div>
    </FilterPanel>
  );
};
